import type { RefObject } from "react";
import { Alert, Box, Tab, Tabs, Typography } from "@mui/material";
import { CustomButton } from "../../../components/common/customButton";
import {
  BOX_SIGN_IN_LABEL,
  getBoxEmbedUrl,
  getBoxFolderUrl,
} from "@/config/boxIntegration";

export type DealBoxFolderOption = {
  key: string;
  label: string;
  folderId: number | null;
};

type DealBoxFilesTabProps = {
  filesRef: RefObject<HTMLDivElement | null>;
  boxFolderOptions: DealBoxFolderOption[];
  selectedBoxFolder: DealBoxFolderOption | undefined;
  onSelectedBoxFolderKeyChange: (key: string) => void;
  boxIframeKey: number;
  onRefreshIframe: () => void;
};

export function DealBoxFilesTab({
  filesRef,
  boxFolderOptions,
  selectedBoxFolder,
  onSelectedBoxFolderKeyChange,
  boxIframeKey,
  onRefreshIframe,
}: DealBoxFilesTabProps) {
  return (
    <Box ref={filesRef} sx={{ mt: 0 }}>
      <Box
        sx={{
          height: "100%",
          backgroundColor: "#fff",
          boxShadow: 1,
          borderRadius: 2,
          paddingTop: 1,
        }}
      >
        {boxFolderOptions.length > 1 ? (
          <Tabs
            value={selectedBoxFolder?.key ?? "deal"}
            onChange={(_, next) => onSelectedBoxFolderKeyChange(next)}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ className: "tf-tabs__indicator" }}
            sx={{
              px: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            {boxFolderOptions.map((option) => (
              <Tab
                key={option.key}
                className="tf-tab tf-tab--compact"
                value={option.key}
                label={option.label}
              />
            ))}
          </Tabs>
        ) : null}
        <Alert severity="info" sx={{ m: 2, mb: 1, mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            If Box fails to load, open Box in a new tab and sign in with{" "}
            {BOX_SIGN_IN_LABEL}. Then click Refresh below.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <CustomButton
              text="Open Box in new tab"
              handleButtonPress={() =>
                window.open(
                  getBoxFolderUrl(selectedBoxFolder?.folderId),
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              isButtonDisabled={!selectedBoxFolder?.folderId}
              solid={false}
            />
            <CustomButton
              text="Refresh"
              handleButtonPress={onRefreshIframe}
              isButtonDisabled={!selectedBoxFolder?.folderId}
              solid={false}
            />
          </Box>
        </Alert>
        {selectedBoxFolder?.folderId ? (
          <iframe
            key={`${boxIframeKey}-${selectedBoxFolder.key}`}
            src={getBoxEmbedUrl(selectedBoxFolder.folderId)}
            width="100%"
            height="700"
            allowFullScreen
            style={{ border: "none", padding: "0px", margin: "0px" }}
            title={`Box folder — ${selectedBoxFolder.label}`}
          />
        ) : (
          <Typography
            sx={{ px: 2, pb: 2, fontSize: "14px", color: "#182737" }}
          >
            No Box folder is linked
            {selectedBoxFolder?.key === "deal"
              ? " for this deal"
              : " for this addon"}
            yet.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
